import { useState, useEffect, useRef, Fragment } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import RunPodDirect from '../lib/runpod-direct'
import type { FeatureKey } from '../lib/types'
import { ansiToHtml } from '../lib/ansiToHtml'
import { 
  CONFIG_DEFINITIONS, 
  ConfigState, 
  ConfigGroup,
  ConfigSubsection,
  ConfigDefinition,
  getDefaultValue,
  configToEnvVars,
  generateCommandPreview,
  getMissingMandatoryFields,
  isMandatoryAndMissing,
  isConfigSupported,
  groupSupportedConfigs
} from '../lib/configDefinitions'

type ExecutionMode = 'local' | 'runpod'

export default function LaunchPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const outputRef = useRef<HTMLDivElement>(null)
  
  // Get configuration from navigation state
  const launchConfig = location.state as any
  
  const [mode, setMode] = useState<ExecutionMode>('local')
  const [isRunning, setIsRunning] = useState(false)
  const [output, setOutput] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [config, setConfig] = useState<ConfigState>({})
  const [commandPreview, setCommandPreview] = useState<string>('')
  const [inputValue, setInputValue] = useState<string>('')
  const [currentLine, setCurrentLine] = useState<string>('')  // For progress bar updates
  
  // New state for execution phases
  const [selectedPhases, setSelectedPhases] = useState<string[]>(['reasoning', 'analyzers'])
  const [phaseOrder, setPhaseOrder] = useState<string[]>(['reasoning', 'analyzers', 'trainers'])
  
  // Check if we're running locally or on Netlify
  useEffect(() => {
    const isLocal = window.location.hostname === 'localhost' || 
                   window.location.hostname === '127.0.0.1'
    setMode(isLocal ? 'local' : 'runpod')
  }, [])
  
  // Initialize configuration from launch state
  useEffect(() => {
    // Even if launchConfig is empty, we should initialize with defaults
    const initialConfig: ConfigState = {}
    
    // Map features from IndexPage selection (not editable)
    const featureMap: Record<FeatureKey, string> = {
      'Evaluation Strategy': 'ALGO_NAMES',
      'Domain': 'DOMAIN_NAMES',
      'Reasoning Mode': 'REASONING_MODE_NAMES',
      'Model': 'POLICY_LIST',
      'System Prompt': 'SYSTEM_PROMPTS',
      'Result Analyzer': 'ANALYZERS'  // Changed from RESULT_ANALYZERS to ANALYZERS
    }
    
    // Get default options for features from CONFIG_DEFINITIONS
    const getDefaultOptions = (configKey: string): string[] => {
      const def = CONFIG_DEFINITIONS.find(d => d.key === configKey)
      return def?.options || []
    }
    
    // Get all available values from the data passed from IndexPage
    const getAllAvailableValues = (feature: FeatureKey): string[] => {
      // If we have data from IndexPage, use it
      if (launchConfig?.availableValues?.[feature]) {
        return Array.from(launchConfig.availableValues[feature])
      }
      // Otherwise, for predefined options, use the config definition
      const configKey = featureMap[feature]
      if (configKey === 'ALGO_NAMES' || configKey === 'DOMAIN_NAMES' || 
          configKey === 'REASONING_MODE_NAMES' || configKey === 'ANALYZERS') {
        return getDefaultOptions(configKey)
      }
      // For Model and System Prompt, we'll need to get from data
      // If not available, return empty
      return []
    }
    
    // FIRST PASS: Set feature values with select-all logic applied early
    for (const [feature, configKey] of Object.entries(featureMap)) {
      const activeValues = launchConfig?.activeFilters?.[feature as FeatureKey] || new Set()
      const pendingValues = launchConfig?.pendingLaunches?.[feature as FeatureKey] || new Set()
      
      const allValues = new Set([...activeValues, ...pendingValues])
      
      // If no selection, treat as "select all"
      let valuesArray: string[]
      if (allValues.size === 0) {
        // Get all available values for this feature
        valuesArray = getAllAvailableValues(feature as FeatureKey)
        
        // Special handling for ANALYZERS - if empty selection means "all", use that
        if (configKey === 'ANALYZERS' && valuesArray.length === 0) {
          // If no analyzers are selected and no available values, default to 'all'
          valuesArray = ['all']
        }
      } else {
        valuesArray = Array.from(allValues)
      }
      
      // Set values in config (this ensures hasAlgorithm/hasAnalyzer work properly)
      if (valuesArray.length > 0) {
        // Handle special mappings
        if (configKey === 'POLICY_LIST' && feature === 'Model') {
          // Models are comma-separated string
          initialConfig[configKey] = valuesArray.join(',')
        } else if (configKey === 'SYSTEM_PROMPTS' && feature === 'System Prompt') {
          // System prompts are comma-separated string
          initialConfig[configKey] = valuesArray.join(',')
        } else {
          // For multiselect features like ALGO_NAMES, DOMAIN_NAMES, ANALYZERS
          initialConfig[configKey] = valuesArray
        }
      } else if (configKey === 'POLICY_LIST' || configKey === 'SYSTEM_PROMPTS') {
        // For free-text fields, if no values available, leave empty
        initialConfig[configKey] = ''
      } else if (configKey === 'ANALYZERS') {
        // Default analyzers to 'all' if nothing selected
        initialConfig[configKey] = ['all']
      }
    }
    
    // Initialize all other fields with defaults
    // Process mandatory fields first, then optional
    const mandatoryDefs = CONFIG_DEFINITIONS.filter(d => d.mandatory && !initialConfig[d.key])
    const optionalDefs = CONFIG_DEFINITIONS.filter(d => !d.mandatory && !initialConfig[d.key])
    
    // Set defaults for mandatory fields
    for (const def of mandatoryDefs) {
      if (!initialConfig[def.key]) {
        const defaultVal = getDefaultValue(def.key, initialConfig)
        if (defaultVal !== undefined) {
          initialConfig[def.key] = defaultVal
        }
      }
    }
    
    // Set defaults for optional fields
    // Only set if defaultValue is explicitly defined (not undefined)
    for (const def of optionalDefs) {
      const defaultVal = getDefaultValue(def.key, initialConfig)
      if (defaultVal !== undefined) {
        initialConfig[def.key] = defaultVal
      }
      // If defaultVal is undefined, don't add it to initialConfig
      // This allows the config to remain unset until user explicitly sets it
    }
    
    setConfig(initialConfig)
  }, [launchConfig])
  
  // Update command preview when config or phases change
  useEffect(() => {
    setCommandPreview(generateCommandPreview(config, selectedPhases))
  }, [config, selectedPhases])
  
  // Handle config value change
  const handleConfigChange = (key: string, value: any) => {
    setConfig(prev => {
      const newConfig = { ...prev, [key]: value }
      
      // Re-evaluate defaults that depend on this change
      // For example, OPENROUTER_API_KEY becomes mandatory when USE_OPENROUTER is true
      if (key === 'USE_OPENROUTER') {
        const openRouterKeyDef = CONFIG_DEFINITIONS.find(d => d.key === 'OPENROUTER_API_KEY')
        if (openRouterKeyDef) {
          const defaultVal = getDefaultValue('OPENROUTER_API_KEY', newConfig)
          if (defaultVal !== undefined) {
            newConfig['OPENROUTER_API_KEY'] = defaultVal
          }
        }
      }
      
      return newConfig
    })
  }
  
  // Render config input based on type
  const renderConfigInput = (def: any) => {
    const value = config[def.key]
    const isMissing = isMandatoryAndMissing(def.key, config)
    
    switch (def.type) {
      case 'boolean':
        return (
          <input
            type="checkbox"
            checked={value === true || value === 1 || value === '1'}
            onChange={(e) => handleConfigChange(def.key, e.target.checked)}
            disabled={def.editable === false}
          />
        )
      
      case 'number':
        return (
          <input
            type="number"
            value={typeof value === 'number' ? value : ''}
            onChange={(e) => handleConfigChange(def.key, e.target.value ? Number(e.target.value) : undefined)}
            disabled={def.editable === false}
            style={{
              width: '100%',
              padding: '4px 8px',
              border: isMissing ? '1px solid #dc2626' : '1px solid #e5e7eb',
              borderRadius: 4,
              fontSize: 12
            }}
          />
        )
      
      case 'select':
        return (
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleConfigChange(def.key, e.target.value)}
            disabled={def.editable === false}
            style={{
              width: '100%',
              padding: '4px 8px',
              border: isMissing ? '1px solid #dc2626' : '1px solid #e5e7eb',
              borderRadius: 4,
              fontSize: 12
            }}
          >
            <option value="">-- Select --</option>
            {def.options?.map((opt: string) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        )
      
      case 'multiselect':
        const arrayValue = Array.isArray(value) ? value : []
        return (
          <div style={{ 
            border: isMissing ? '1px solid #dc2626' : '1px solid #e5e7eb',
            borderRadius: 4,
            padding: 4,
            minHeight: 30,
            background: def.editable === false ? '#f9fafb' : 'white'
          }}>
            {arrayValue.map((v: string) => (
              <span
                key={v}
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  margin: 2,
                  background: '#e5e7eb',
                  borderRadius: 4,
                  fontSize: 11
                }}
              >
                {v}
                {def.editable !== false && (
                  <button
                    onClick={() => handleConfigChange(def.key, arrayValue.filter(x => x !== v))}
                    style={{ marginLeft: 4, cursor: 'pointer', border: 'none', background: 'none' }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {def.editable !== false && def.options && (
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value && !arrayValue.includes(e.target.value)) {
                    handleConfigChange(def.key, [...arrayValue, e.target.value])
                  }
                }}
                style={{ fontSize: 11, marginTop: 4 }}
              >
                <option value="">+ Add</option>
                {def.options.filter((opt: string) => !arrayValue.includes(opt)).map((opt: string) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            )}
          </div>
        )
      
      default: // string
        return (
          <input
            type="text"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleConfigChange(def.key, e.target.value)}
            disabled={def.editable === false}
            placeholder={def.key === 'OPENROUTER_API_KEY' && isMissing ? 'Required when USE_OPENROUTER=true' : ''}
            style={{
              width: '100%',
              padding: '4px 8px',
              border: isMissing ? '1px solid #dc2626' : '1px solid #e5e7eb',
              borderRadius: 4,
              fontSize: 12
            }}
          />
        )
    }
  }
  
  // Filter configs based on execution mode and commandTarget
  const filterConfigsByExecutionMode = (configs: typeof CONFIG_DEFINITIONS) => {
    return configs.filter(def => {
      // Default commandTarget is 'reasoning' if not specified
      const target = def.commandTarget || 'both'
      
      // Show config if any of the selected phases are in its commandTarget
      const targetArray = Array.isArray(target) ? target : [target]
      return selectedPhases.some(phase => targetArray.includes(phase))
    })
  }
  
  // Group configurations by category and subsection, only showing supported ones
  const groupedConfigs = groupSupportedConfigs(config)
  
  // Apply execution mode filtering to the grouped configs
  const filteredGroupedConfigs = {
    features: groupedConfigs.features ? {
      ...Object.fromEntries(
        Object.entries(groupedConfigs.features).map(([key, configs]) => [
          key,
          filterConfigsByExecutionMode(configs as ConfigDefinition[])
        ])
      )
    } : {},
    execution: groupedConfigs.execution ? {
      ...Object.fromEntries(
        Object.entries(groupedConfigs.execution).map(([key, configs]) => [
          key,
          filterConfigsByExecutionMode(configs as ConfigDefinition[])
        ])
      )
    } : {},
    experimental: groupedConfigs.experimental ? {
      ...Object.fromEntries(
        Object.entries(groupedConfigs.experimental).map(([key, configs]) => [
          key,
          filterConfigsByExecutionMode(configs as ConfigDefinition[])
        ])
      )
    } : {}
  }
  
  // Helper to render a subsection
  const renderSubsection = (
    configs: typeof CONFIG_DEFINITIONS, 
    subsectionTitle?: string
  ) => {
    if (!configs || configs.length === 0) return null
    
    return (
      <>
        {subsectionTitle && (
          <h5 style={{ 
            marginTop: subsectionTitle ? 16 : 0, 
            marginBottom: 8, 
            fontSize: 12, 
            fontWeight: 600 
          }}>
            {subsectionTitle}
          </h5>
        )}
        {configs.map(def => {
          const isMissing = isMandatoryAndMissing(def.key, config)
          return (
            <div key={def.key} style={{ marginBottom: 12 }}>
              <label style={{ 
                display: 'block', 
                fontSize: 11, 
                fontWeight: 500, 
                marginBottom: 4,
                color: isMissing ? '#dc2626' : '#374151'
              }}>
                {def.key}
                {def.mandatory && <span style={{ color: '#dc2626' }}> *</span>}
              </label>
              {renderConfigInput(def)}
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                {def.description}
              </div>
            </div>
          )
        })}
      </>
    )
  }
  
  // Execute locally
  const executeLocal = async () => {
    // Check for missing mandatory fields
    const missingFields = getMissingMandatoryFields(config)
    if (missingFields.length > 0) {
      setError(`Missing mandatory fields: ${missingFields.join(', ')}`)
      return
    }
    
    setIsRunning(true)
    setOutput(['Starting local execution...\n'])
    
    try {
      // Get all environment variables based on execution mode
      const allEnvVars = configToEnvVars(config, selectedPhases)
      
      // Send all env vars directly to the backend, plus execution mode
      const execConfig = {
        env_vars: allEnvVars,
        execution_mode: selectedPhases.join(',')
      }
      
      // Start execution
      const response = await fetch('/api/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(execConfig)
      })
      
      const { session_id } = await response.json()
      
      // Stream output using Server-Sent Events
      const eventSource = new EventSource(`/api/stream?session_id=${session_id}`)
      
      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data)
        
        if (data.type === 'output') {
          // Handle carriage return for progress bars
          if (data.data.includes('\r')) {
            // Update the current line (for progress bars)
            const parts = data.data.split('\r')
            const lastPart = parts[parts.length - 1]
            if (lastPart) {
              setCurrentLine(lastPart)
            }
            // Only add complete lines to output
            const completeParts = parts.slice(0, -1)
            if (completeParts.length > 0) {
              setOutput(prev => {
                // If the last output was a progress line, replace it
                const newOutput = [...prev]
                if (newOutput.length > 0 && !newOutput[newOutput.length - 1].endsWith('\n')) {
                  newOutput[newOutput.length - 1] = completeParts.join('')
                } else {
                  newOutput.push(...completeParts)
                }
                return newOutput
              })
            }
          } else {
            // Regular output
            setOutput(prev => {
              const newOutput = [...prev]
              // If we have a current line (from progress bar), append to it
              if (currentLine) {
                if (newOutput.length > 0 && !newOutput[newOutput.length - 1].endsWith('\n')) {
                  newOutput[newOutput.length - 1] = currentLine + data.data
                } else {
                  newOutput.push(currentLine + data.data)
                }
                setCurrentLine('')
              } else {
                newOutput.push(data.data)
              }
              return newOutput
            })
          }
          // Auto-scroll to bottom
          if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight
          }
        } else if (data.type === 'complete') {
          setOutput(prev => [...prev, '\n✅ Execution completed successfully\n'])
          eventSource.close()
          setIsRunning(false)
        } else if (data.type === 'error') {
          setOutput(prev => [...prev, `\n❌ Error: ${data.data}\n`])
          eventSource.close()
          setIsRunning(false)
        }
      }
      
      eventSource.onerror = () => {
        setOutput(prev => [...prev, '\n❌ Connection lost\n'])
        eventSource.close()
        setIsRunning(false)
      }
      
    } catch (err: any) {
      setError(err.message)
      setIsRunning(false)
    }
  }
  
  // Execute on RunPod
  const executeRunPod = async () => {
    // Check for missing mandatory fields
    const missingFields = getMissingMandatoryFields(config)
    if (missingFields.length > 0) {
      setError(`Missing mandatory fields: ${missingFields.join(', ')}`)
      return
    }
    
    // Get API key
    let apiKey = localStorage.getItem('RUNPOD_API_KEY')
    if (!apiKey) {
      apiKey = prompt('Enter your RunPod API key:')
      if (!apiKey) return
      localStorage.setItem('RUNPOD_API_KEY', apiKey)
    }
    
    setIsRunning(true)
    setOutput(['Starting RunPod execution...\n'])
    
    try {
      const runpod = new RunPodDirect({ apiKey })
      
      // Get all environment variables based on execution mode
      const allEnvVars = configToEnvVars(config, selectedPhases)
      
      // Send env vars directly to RunPod
      const execConfig = {
        env_vars: allEnvVars
      }
      
      // Create pod
      setOutput(prev => [...prev, 'Creating pod...\n'])
      const podId = await runpod.createPod(execConfig as any)
      setOutput(prev => [...prev, `Pod created: ${podId}\n`])
      
      // Wait for pod to be ready
      setOutput(prev => [...prev, 'Waiting for pod to be ready...\n'])
      let attempts = 0
      while (attempts < 60) {
        const status = await runpod.getPodStatus(podId)
        if (status === 'RUNNING') {
          setOutput(prev => [...prev, 'Pod is ready!\n\n'])
          break
        }
        await new Promise(resolve => setTimeout(resolve, 5000))
        attempts++
      }
      
      // Stream logs
      for await (const chunk of runpod.streamLogs(podId)) {
        setOutput(prev => [...prev, chunk])
        if (outputRef.current) {
          outputRef.current.scrollTop = outputRef.current.scrollHeight
        }
      }
      
      // Cleanup
      setOutput(prev => [...prev, '\nTerminating pod...\n'])
      await runpod.terminatePod(podId)
      setOutput(prev => [...prev, '✅ Execution completed\n'])
      
    } catch (err: any) {
      setError(err.message)
      setOutput(prev => [...prev, `\n❌ Error: ${err.message}\n`])
    } finally {
      setIsRunning(false)
    }
  }
  
  const handleExecute = () => {
    if (mode === 'local') {
      executeLocal()
    } else {
      executeRunPod()
    }
  }
  
  const handleStop = async () => {
    if (mode === 'local') {
      await fetch('/api/stop', { method: 'POST' })
      setOutput(prev => [...prev, '\n⚠️ Execution stopped\n'])
      setIsRunning(false)
    }
  }
  
  const handleSendInput = async () => {
    if (mode === 'local' && inputValue.trim()) {
      try {
        // Show the input in the output
        setOutput(prev => [...prev, `> ${inputValue}\n`])
        
        // Send to backend
        await fetch('/api/input', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ input: inputValue })
        })
        
        // Clear input field
        setInputValue('')
      } catch (err) {
        console.error('Failed to send input:', err)
      }
    }
  }
  
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendInput()
    }
  }
  
  // No longer block if launchConfig is empty - we'll use defaults
  
  return (
    <div className="app" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="header">
        <div className="title">Execute Run</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ 
            padding: '4px 12px', 
            background: mode === 'local' ? '#10b981' : '#3b82f6',
            color: 'white',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600
          }}>
            {mode === 'local' ? 'LOCAL' : 'RUNPOD'}
          </span>
          <button 
            className="btn btn-ghost"
            onClick={() => navigate('/')}
            disabled={isRunning}
          >
            ← Back
          </button>
        </div>
      </div>
      
      {error && (
        <div style={{ 
          padding: 12, 
          background: '#fee', 
          color: '#dc2626',
          borderBottom: '1px solid #fca5a5'
        }}>
          Error: {error}
        </div>
      )}

      {/* Execution mode switch */}
      <div style={{
        background: '#f9fafb',
        padding: '12px 20px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: selectedPhases.length > 0 ? 12 : 0
        }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Execution Phases:</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`btn-sm-execution ${selectedPhases.includes('reasoning') ? 'active' : ''}`}
              onClick={() => {
                if (selectedPhases.includes('reasoning')) {
                  setSelectedPhases(selectedPhases.filter(p => p !== 'reasoning'))
                } else {
                  setSelectedPhases([...selectedPhases, 'reasoning'])
                }
              }}
              disabled={isRunning}
            >
              Rollout
            </button>
            <button
              className={`btn-sm-execution ${selectedPhases.includes('analyzers') ? 'active' : ''}`}
              onClick={() => {
                if (selectedPhases.includes('analyzers')) {
                  setSelectedPhases(selectedPhases.filter(p => p !== 'analyzers'))
                } else {
                  setSelectedPhases([...selectedPhases, 'analyzers'])
                }
              }}
              disabled={isRunning}
            >
              Analysis
            </button>
            <button
              className={`btn-sm-execution ${selectedPhases.includes('trainers') ? 'active' : ''}`}
              onClick={() => {
                if (selectedPhases.includes('trainers')) {
                  setSelectedPhases(selectedPhases.filter(p => p !== 'trainers'))
                } else {
                  setSelectedPhases([...selectedPhases, 'trainers'])
                }
              }}
              disabled={isRunning}
            >
              Training
            </button>
          </div>
          {selectedPhases.length === 0 && (
            <span style={{ fontSize: 13, color: '#ef4444', fontStyle: 'italic' }}>
              Please select at least one phase to execute
            </span>
          )}
        </div>
        {selectedPhases.length > 1 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16
          }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Execution Order:</span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {phaseOrder.filter(p => selectedPhases.includes(p)).map((phase, index, arr) => (
                <Fragment key={phase}>
                  <div
                    draggable={!isRunning}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('phase', phase)
                      e.currentTarget.style.opacity = '0.5'
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1'
                    }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      const draggedPhase = e.dataTransfer.getData('phase')
                      if (draggedPhase !== phase) {
                        const newOrder = [...phaseOrder]
                        const draggedIndex = newOrder.indexOf(draggedPhase)
                        const targetIndex = newOrder.indexOf(phase)
                        newOrder.splice(draggedIndex, 1)
                        newOrder.splice(targetIndex, 0, draggedPhase)
                        setPhaseOrder(newOrder)
                      }
                    }}
                    style={{
                      padding: '6px 14px',
                      background: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      cursor: isRunning ? 'not-allowed' : 'move',
                      fontSize: 13,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      transition: 'all 0.2s ease',
                      userSelect: 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!isRunning) {
                        e.currentTarget.style.background = '#f3f4f6'
                        e.currentTarget.style.borderColor = '#d1d5db'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#ffffff'
                      e.currentTarget.style.borderColor = '#e5e7eb'
                    }}
                  >
                    <span style={{
                      background: '#6366f1',
                      color: 'white',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 600
                    }}>
                      {index + 1}
                    </span>
                    {phase === 'reasoning' ? 'Rollout' : phase === 'analyzers' ? 'Analysis' : 'Training'}
                  </div>
                  {index < arr.length - 1 && (
                    <span style={{ color: '#9ca3af', fontSize: 18 }}>→</span>
                  )}
                </Fragment>
              ))}
              {selectedPhases.length > 1 && (
                <span style={{
                  fontSize: 11,
                  color: '#6b7280',
                  fontStyle: 'italic',
                  marginLeft: 8
                }}>
                  (drag to reorder)
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '0 20px', overflow: 'auto' }}>
        {/* Configuration Editor */}
        <div style={{ marginBottom: 20 }}>
          <h3>Configuration</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 20,
            marginTop: 12
          }}>
            {/* Features Column (not editable) */}
            <div>
              <h4 style={{ marginBottom: 8, fontSize: 14, fontWeight: 600 }}>Features (from selection)</h4>
              <div style={{ background: '#f9fafb', padding: 12, borderRadius: 8 }}>
                {renderSubsection(filteredGroupedConfigs.features?.default)}
              </div>
            </div>
            
            {/* Execution Column */}
            <div>
              <h4 style={{ marginBottom: 8, fontSize: 14, fontWeight: 600 }}>Execution</h4>
              <div style={{ 
                background: '#ffffff', 
                padding: 12, 
                borderRadius: 8, 
                border: '1px solid #e5e7eb',
                maxHeight: '70vh',
                overflow: 'auto'
              }}>
                {/* Default execution settings */}
                {renderSubsection(filteredGroupedConfigs.execution?.default)}
                
                {/* Subsections within execution */}
                {Object.entries(filteredGroupedConfigs.execution || {}).map(([subsection, configs]) => {
                  if (subsection === 'default' || !configs || !Array.isArray(configs) || configs.length === 0) return null
                  return renderSubsection(configs as ConfigDefinition[], subsection)
                })}
              </div>
            </div>
            
            {/* Experimental Column */}
            <div>
              <h4 style={{ marginBottom: 8, fontSize: 14, fontWeight: 600 }}>Experimental</h4>
              <div style={{ 
                background: '#ffffff', 
                padding: 12, 
                borderRadius: 8, 
                border: '1px solid #e5e7eb',
                maxHeight: '70vh',
                overflow: 'auto'
              }}>
                {/* Default experimental settings */}
                {renderSubsection(filteredGroupedConfigs.experimental?.default)}
                
                {/* Subsections within experimental */}
                {Object.entries(filteredGroupedConfigs.experimental || {}).map(([subsection, configs]) => {
                  if (subsection === 'default' || !configs || !Array.isArray(configs) || configs.length === 0) return null
                  return renderSubsection(configs as ConfigDefinition[], subsection)
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Command Preview */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Command Preview</h3>
            <button
              className="btn btn-ghost"
              onClick={() => {
                navigator.clipboard.writeText(commandPreview)
                  .then(() => {
                    // Optional: Add a temporary "Copied!" indicator
                    const btn = event?.target as HTMLButtonElement
                    if (btn) {
                      const originalText = btn.textContent
                      btn.textContent = '✓ Copied!'
                      setTimeout(() => { btn.textContent = originalText }, 2000)
                    }
                  })
                  .catch(err => console.error('Failed to copy:', err))
              }}
              style={{ fontSize: 12, padding: '4px 8px' }}
            >
              📋 Copy
            </button>
          </div>
          <pre style={{ 
            background: '#1e1e1e', 
            color: '#d4d4d4',
            padding: 12, 
            borderRadius: 8,
            fontSize: 11,
            minHeight: '30vh',
            maxHeight: '40vh',
            overflow: 'auto',
            fontFamily: 'Menlo, Monaco, monospace',
            margin: 0
          }}>
            {commandPreview}
          </pre>
        </div>
        
        {/* Control Buttons */}
        <div style={{ marginBottom: 20, display: 'flex', gap: 12 }}>
          <button 
            className="btn btn-primary"
            onClick={handleExecute}
            disabled={isRunning || getMissingMandatoryFields(config).length > 0}
          >
            {isRunning ? '🔄 Running...' : '🚀 Execute'}
          </button>
          
          {getMissingMandatoryFields(config).length > 0 && (
            <span style={{ 
              color: '#dc2626', 
              fontSize: 12, 
              alignSelf: 'center' 
            }}>
              Missing mandatory fields: {getMissingMandatoryFields(config).join(', ')}
            </span>
          )}
          
          {mode === 'local' && isRunning && (
            <button 
              className="btn btn-ghost"
              onClick={handleStop}
            >
              ⏹ Stop
            </button>
          )}
          
          {mode === 'runpod' && (
            <button 
              className="btn btn-ghost"
              onClick={() => {
                const key = prompt('Enter new RunPod API key:')
                if (key) localStorage.setItem('RUNPOD_API_KEY', key)
              }}
            >
              🔑 Set API Key
            </button>
          )}
        </div>
        
        {/* Output Terminal */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '80vh' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Output</h3>
            <button
              className="btn btn-ghost"
              onClick={() => {
                const outputText = output.join('') + (currentLine || '')
                navigator.clipboard.writeText(outputText)
                  .then(() => {
                    // Optional: Add a temporary "Copied!" indicator
                    const btn = event?.target as HTMLButtonElement
                    if (btn) {
                      const originalText = btn.textContent
                      btn.textContent = '✓ Copied!'
                      setTimeout(() => { btn.textContent = originalText }, 2000)
                    }
                  })
                  .catch(err => console.error('Failed to copy:', err))
              }}
              style={{ fontSize: 12, padding: '4px 8px' }}
            >
              📋 Copy
            </button>
          </div>
          <div 
            ref={outputRef}
            style={{
              flex: 1,
              background: '#1e1e1e',
              color: '#d4d4d4',
              padding: 12,
              borderRadius: 8,
              fontFamily: 'Menlo, Monaco, monospace',
              fontSize: 12,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              minHeight: '70vh',
              maxHeight: '100vh'
            }}
            dangerouslySetInnerHTML={{
              __html: ansiToHtml(output.join('') + (currentLine || ''))
            }}
          />
          {/* Input field for interaction */}
          {mode === 'local' && isRunning && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type input and press Enter (or click Send)..."
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: '#2a2a2a',
                  color: '#d4d4d4',
                  border: '1px solid #444',
                  borderRadius: 4,
                  fontFamily: 'Menlo, Monaco, monospace',
                  fontSize: 12
                }}
              />
              <button
                className="btn btn-ghost"
                onClick={handleSendInput}
                style={{ fontSize: 12 }}
              >
                Send
              </button>
              <button
                className="btn btn-ghost"
                onClick={handleStop}
                title="Send interrupt signal (Ctrl+C)"
                style={{ fontSize: 12, color: '#dc2626' }}
              >
                Interrupt (Ctrl+C)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}